import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { routeConstants } from '../../../../constants/routeConstants';
import { useGetProductCategoriesQuery } from '../../../../services/Sublocation/SublocationService';
import {
  useFinalizeMonthlyReconciliationMutation,
  useGetMonthlyReconciliationFeedsMutation,
} from '../../../../services/GlPostings/GlPostingsService';
import { buildProductTypeSelectData, DATE_FORMAT_API } from '../utils/monthlyReconciliationProductHelpers';
import { getMonthlyFeedsErrorMessage } from '../utils/monthlyReconciliationValidationHelpers';
import {
  buildAppliedMonthlyFilters,
  buildMonthlyRequestPayload,
  EMPTY_MONTHLY_ERROR_MODAL,
  formatMonthlyUiDate,
  getFinalizedWindowAlertState,
  getMonthlyValidationModalState,
  mapMonthlySummaryToReconciliationData,
  mapMonthlyFeedsResponseToSummary,
  mapMonthlySummaryToWeeklyPostingsData,
  toMonthlyApiDate,
} from '../helpers/monthlyReconciliationDetailHelpers';
import {
  clearMonthlyRestoreIntent,
  getRestoredMonthlyState,
  isMonthlyRestoreIntentActive,
  setMonthlyRestoreIntent,
  writeMonthlyState,
} from '../helpers/monthlyReconciliationSessionHelpers';

import { DATE_CONSTANTS } from '../constants';

const MONTHLY_RECONCILIATION_MIN_DATE = dayjs(DATE_CONSTANTS.START_DATE_MIN);
const INVALID_FILTERS_MESSAGE = 'Invalid reconciliation filters.';

export const useMonthlyReconciliationDetailController = () => {
  const navigate = useNavigate();
  const { data: productCategories = [] } = useGetProductCategoriesQuery({ isGLPostingEnabled: true, isMonthly: true });
  const [getMonthlyFeeds] = useGetMonthlyReconciliationFeedsMutation();
  const [finalizeMonthlyReconciliation, { isLoading: isFinalizingReconciliation }] =
    useFinalizeMonthlyReconciliationMutation();

  const restoredStateRef = useRef(undefined);
  if (restoredStateRef.current === undefined) {
    restoredStateRef.current = getRestoredMonthlyState();
  }

  const saved = restoredStateRef.current;
  const didRestoreWithData = Boolean(saved?.appliedFilters && saved?.reconciliationSummary);
  const didRestoreWithoutSummary = Boolean(saved?.appliedFilters && !saved?.reconciliationSummary);

  const [selectedProductType, setSelectedProductType] = useState(() =>
    saved?.appliedFilters ? String(saved.appliedFilters.productId ?? '') : '',
  );
  const [startDate, setStartDate] = useState(() => saved?.appliedFilters?.startDate ?? null);
  const [endDate, setEndDate] = useState(() => saved?.appliedFilters?.endDate ?? null);
  const [postingDate, setPostingDate] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(() => saved?.appliedFilters ?? null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isWarningsExpanded, setIsWarningsExpanded] = useState(true);
  const [reconciliationSummary, setReconciliationSummary] = useState(() => saved?.reconciliationSummary ?? null);
  const [feedsError, setFeedsError] = useState(null);
  const [errorModal, setErrorModal] = useState(EMPTY_MONTHLY_ERROR_MODAL);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [finalizeError, setFinalizeError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [finalizedData, setFinalizedData] = useState(
    () => saved?.reconciliationSummary?.finalizedWindow?.posting ?? null,
  );
  const skipFetchAfterRestoreRef = useRef(didRestoreWithData);

  const productSelectData = useMemo(() => buildProductTypeSelectData(productCategories), [productCategories]);
  const areDateFieldsEnabled = Boolean(selectedProductType);
  const maxSelectableDate = useMemo(() => dayjs(), []);
  const startDateMax = useMemo(() => {
    const parsedEndDate = dayjs(endDate);
    return parsedEndDate.isValid() && parsedEndDate.isBefore(maxSelectableDate) ? parsedEndDate : maxSelectableDate;
  }, [endDate, maxSelectableDate]);
  const endDateMin = useMemo(() => {
    const parsedStartDate = dayjs(startDate);
    return parsedStartDate.isValid() && parsedStartDate.isAfter(MONTHLY_RECONCILIATION_MIN_DATE)
      ? parsedStartDate
      : MONTHLY_RECONCILIATION_MIN_DATE;
  }, [startDate]);

  const clearReconciliationState = useCallback(() => {
    setAppliedFilters(null);
    setReconciliationSummary(null);
    setFinalizedData(null);
    setFeedsError(null);
    setErrorModal(EMPTY_MONTHLY_ERROR_MODAL);
  }, []);

  const applyReconciliationSummary = useCallback((summary) => {
    setFeedsError(null);
    setErrorModal(EMPTY_MONTHLY_ERROR_MODAL);
    setReconciliationSummary(summary);
    setFinalizedData(summary?.finalizedWindow?.posting ?? null);
  }, []);

  const handleMonthlyValidationError = useCallback((error) => {
    const modalState = getMonthlyValidationModalState(error);

    if (modalState) {
      setErrorModal(modalState);
      setFeedsError(null);
      return;
    }

    setErrorModal(EMPTY_MONTHLY_ERROR_MODAL);
    setFeedsError(getMonthlyFeedsErrorMessage(error?.data || error));
  }, []);

  const requestReconciliationSummary = useCallback(
    async (filters) => {
      const requestBody = buildMonthlyRequestPayload({ appliedFilters: filters, productCategories });

      if (!requestBody) {
        const invalidFiltersError = new Error(INVALID_FILTERS_MESSAGE);
        invalidFiltersError.code = 'INVALID_FILTERS';
        throw invalidFiltersError;
      }

      const data = await getMonthlyFeeds({
        productId: requestBody.productId,
        x3ProductName: requestBody.x3ProductName,
        startDate: requestBody.startDate,
        endDate: requestBody.endDate,
      }).unwrap();

      return mapMonthlyFeedsResponseToSummary(data, requestBody.x3ProductName);
    },
    [getMonthlyFeeds, productCategories],
  );

  const handleSummaryRequestError = useCallback(
    (error) => {
      setReconciliationSummary(null);
      setFinalizedData(null);

      if (error?.code === 'INVALID_FILTERS') {
        setErrorModal(EMPTY_MONTHLY_ERROR_MODAL);
        setFeedsError(error.message);
        return;
      }

      handleMonthlyValidationError(error);
    },
    [handleMonthlyValidationError],
  );

  useEffect(() => {
    if (!didRestoreWithoutSummary) return;
    if (!saved?.appliedFilters) return;

    let cancelled = false;

    setIsSummaryLoading(true);
    setFeedsError(null);

    requestReconciliationSummary(saved.appliedFilters)
      .then((summary) => {
        if (!cancelled) applyReconciliationSummary(summary);
      })
      .catch((error) => {
        if (!cancelled) handleSummaryRequestError(error);
      })
      .finally(() => {
        if (!cancelled) setIsSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    didRestoreWithoutSummary,
    saved?.appliedFilters,
    requestReconciliationSummary,
    applyReconciliationSummary,
    handleSummaryRequestError,
  ]);

  useEffect(() => {
    if (!appliedFilters || !reconciliationSummary) return;
    writeMonthlyState({ appliedFilters, reconciliationSummary });
  }, [appliedFilters, reconciliationSummary]);

  useEffect(() => {
    if (!isMonthlyRestoreIntentActive()) return;
    const timerId = setTimeout(clearMonthlyRestoreIntent, 0);
    return () => clearTimeout(timerId);
  }, []);

  useEffect(
    () => () => {
      const pathname = typeof window !== 'undefined' ? String(window.location?.pathname ?? '') : '';
      const normalizedPathname = pathname.replace(/\/+$/, '');
      const machinesPrefix = `/${routeConstants.MACHINES_ROUTE}/`;

      if (normalizedPathname.startsWith(machinesPrefix)) setMonthlyRestoreIntent();
    },
    [],
  );

  const isApplyEnabled = useMemo(
    () =>
      Boolean(
        selectedProductType &&
          startDate &&
          endDate &&
          dayjs(startDate).isValid() &&
          dayjs(endDate).isValid() &&
          !dayjs(endDate).isBefore(dayjs(startDate)),
      ),
    [selectedProductType, startDate, endDate],
  );

  const handleProductTypeChange = useCallback(
    (event) => {
      setSelectedProductType(event.target.value);
      setStartDate(null);
      setEndDate(null);
      clearReconciliationState();
    },
    [clearReconciliationState],
  );

  const handleStartDateChange = useCallback((newDate) => {
    setStartDate(newDate);
    setEndDate((currentEndDate) =>
      currentEndDate && newDate && dayjs(newDate).isAfter(dayjs(currentEndDate)) ? null : currentEndDate,
    );
  }, []);

  const handleEndDateChange = useCallback(
    (newDate) => {
      if (startDate && newDate && dayjs(newDate).isBefore(dayjs(startDate))) return;
      setEndDate(newDate);
    },
    [startDate],
  );

  const handlePostingDateChange = useCallback((newDate) => {
    setPostingDate(newDate);
  }, []);

  const handleApply = useCallback(() => {
    if (!isApplyEnabled) return;

    const nextAppliedFilters = buildAppliedMonthlyFilters({
      selectedProductType,
      productCategories,
      productNamesById: productSelectData.productNamesById,
      startDate,
      endDate,
    });

    if (!nextAppliedFilters) return;

    skipFetchAfterRestoreRef.current = false;
    setAppliedFilters(nextAppliedFilters);
    setReconciliationSummary(null);
    setFinalizedData(null);
    setFeedsError(null);
    setErrorModal(EMPTY_MONTHLY_ERROR_MODAL);
  }, [isApplyEnabled, selectedProductType, productCategories, productSelectData.productNamesById, startDate, endDate]);

  useEffect(() => {
    if (!appliedFilters || skipFetchAfterRestoreRef.current) return;

    let cancelled = false;

    setIsSummaryLoading(true);
    setFeedsError(null);

    requestReconciliationSummary(appliedFilters)
      .then((summary) => {
        if (!cancelled) applyReconciliationSummary(summary);
      })
      .catch((error) => {
        if (!cancelled) handleSummaryRequestError(error);
      })
      .finally(() => {
        if (!cancelled) setIsSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appliedFilters, requestReconciliationSummary, applyReconciliationSummary, handleSummaryRequestError]);

  useEffect(() => {
    const startNorm = startDate ? dayjs(startDate).format(DATE_FORMAT_API) : '';
    const endNorm = endDate ? dayjs(endDate).format(DATE_FORMAT_API) : '';

    if (
      appliedFilters &&
      startNorm === (appliedFilters.startDate || '') &&
      endNorm === (appliedFilters.endDate || '')
    ) {
      return;
    }

    clearReconciliationState();
  }, [startDate, endDate, appliedFilters, clearReconciliationState]);

  const handleToggleWarnings = useCallback(() => {
    setIsWarningsExpanded((prev) => !prev);
  }, []);

  const handleSuccessModalClose = useCallback(() => {
    setShowSuccessModal(false);
    navigate(`/${routeConstants.MONTHLY_RECONCILIATION_ROUTE}?selectedTab=2`);
  }, [navigate]);

  const handleFinalize = useCallback(() => {
    if (!appliedFilters) {
      setFinalizeError('No reconciliation data available');
      setShowFailureModal(true);
      return;
    }

    if (!postingDate) {
      setFinalizeError('Select a posting date to continue');
      setShowFailureModal(true);
      return;
    }

    setShowConfirmModal(true);
  }, [appliedFilters, postingDate]);

  const handleConfirmFinalize = useCallback(async () => {
    setShowConfirmModal(false);

    try {
      const requestBody = buildMonthlyRequestPayload({ appliedFilters, productCategories });

      if (!requestBody) {
        setFinalizeError('Invalid reconciliation filters');
        setShowFailureModal(true);
        return;
      }

      const normalizedPostingDate = toMonthlyApiDate(postingDate);

      if (!normalizedPostingDate) {
        setFinalizeError('Select a posting date to continue');
        setShowFailureModal(true);
        return;
      }

      const response = await finalizeMonthlyReconciliation({
        ...requestBody,
        postingDate: normalizedPostingDate,
      }).unwrap();

      if (response?.data?.newPosting) {
        setFinalizedData(response.data.newPosting);
      }

      setShowSuccessModal(true);
    } catch (error) {
      const errorData = error?.data || error;
      const validationErrors = errorData?.validationError ?? errorData?.validationErrors ?? [];

      if (validationErrors.length > 0 || error?.status === 422) {
        handleMonthlyValidationError(error);
        return;
      }

      const errorMessage = error?.data?.message || error?.message || 'Failed to finalize monthly reconciliation';
      setFinalizeError(errorMessage);
      setShowFailureModal(true);
    }
  }, [appliedFilters, postingDate, productCategories, finalizeMonthlyReconciliation, handleMonthlyValidationError]);

  const reconciliationData = useMemo(
    () => mapMonthlySummaryToReconciliationData({ appliedFilters, reconciliationSummary }),
    [appliedFilters, reconciliationSummary],
  );
  const weeklyPostingsData = useMemo(
    () => mapMonthlySummaryToWeeklyPostingsData({ appliedFilters, reconciliationSummary }),
    [appliedFilters, reconciliationSummary],
  );
  const finalizedWindowAlert = useMemo(
    () => getFinalizedWindowAlertState({ appliedFilters, reconciliationSummary }),
    [appliedFilters, reconciliationSummary],
  );
  const hasExceptions =
    (reconciliationSummary?.missingDataFeedCount ?? 0) > 0 || (reconciliationSummary?.totalUnmappedDataFeed ?? 0) > 0;

  const formattedPostingDate = useMemo(() => (postingDate ? formatMonthlyUiDate(postingDate) : ''), [postingDate]);

  const closeErrorModal = useCallback(() => setErrorModal(EMPTY_MONTHLY_ERROR_MODAL), []);
  const closeConfirmModal = useCallback(() => setShowConfirmModal(false), []);
  const closeFailureModal = useCallback(() => setShowFailureModal(false), []);

  return {
    controlPanelProps: {
      selectedProductType,
      onProductTypeChange: handleProductTypeChange,
      productTypeOptions: productSelectData.options,
      productNamesById: productSelectData.productNamesById,
      startDate,
      endDate,
      onStartDateChange: handleStartDateChange,
      onEndDateChange: handleEndDateChange,
      postingDate,
      onPostingDateChange: handlePostingDateChange,
      areDateFieldsEnabled,
      minDate: MONTHLY_RECONCILIATION_MIN_DATE,
      startDateMax,
      endDateMin,
      maxSelectableDate,
      dateFormat: DATE_FORMAT_API,
      onApply: handleApply,
      isApplyEnabled,
      resyncData: reconciliationSummary?.resyncData ?? [],
    },
    summaryProps: {
      appliedFilters,
      feedsError,
      finalizedWindowAlert,
      hasExceptions,
      isSummaryLoading,
      reconciliationData,
      reconciliationSummary,
    },
    resultsProps: {
      appliedFilters,
      assetWithVarianceData: reconciliationData?.assetWithVarianceData,
      finalizedData,
      isFinalizingReconciliation,
      isSummaryLoading,
      isWarningsExpanded,
      onAssetLinkClick: setMonthlyRestoreIntent,
      onFinalize: handleFinalize,
      onToggleWarnings: handleToggleWarnings,
      postingDate,
      selectedProductName: appliedFilters?.productName ?? appliedFilters?.productType,
      sublocationWarnings: reconciliationData?.sublocationWarnings,
      weeklyPostingsData,
    },
    finalizeDialogProps: {
      appliedFilters,
      finalizeError,
      finalizedData,
      isFinalizingReconciliation,
      onCloseConfirm: closeConfirmModal,
      onConfirmFinalize: handleConfirmFinalize,
      onFailureClose: closeFailureModal,
      onSuccessClose: handleSuccessModalClose,
      postingDate: formattedPostingDate,
      showConfirmModal,
      showFailureModal,
      showSuccessModal,
    },
    validationDialogProps: {
      errorModal,
      onClose: closeErrorModal,
    },
  };
};
